import {
  BatchApplied, BatchAppliedRow, BatchApplyRequest, BatchKind, BatchRow, BatchSide, PROPOSED_KINDS, ReviewBatch,
} from '../../api/services';

// The batch table's client logic (sc-6441): what the user has typed / unchecked,
// and everything derived from it. The server classifies each row at both
// cadences (ReviewBatch); this only picks a side, applies the user's edits, and
// writes the plain-text sentences. Nothing here depends on the typed number
// except the counts — the Why sentence never does, so nothing moves while
// editing.

export interface BatchEdits {
  cadenceIn: boolean; // the cadence change is included
  values: Record<number, number>; // typed New max by pe (absent = the starting number)
  unchecked: Record<number, boolean>; // proposed rows the customer keeps
  notes: Record<number, string>;
}

export const emptyEdits = (): BatchEdits => ({ cadenceIn: true, values: {}, unchecked: {}, notes: {} });

export interface ViewRow extends BatchRow {
  side: BatchSide;
  kind: BatchKind | 'applied' | 'kept';
  proposed: boolean;
  start: number | null; // the starting New max: the engine's on proposed rows, the max now on the rest
  value: number | null; // what New max shows
  custom: boolean; // a number of the customer's own
  included: boolean;
  appliedRow: BatchAppliedRow | null;
}

export const cadenceChanges = (batch: ReviewBatch) =>
  batch.cadence.recommended_weeks !== batch.cadence.current_weeks;

// Before Apply: the toggle. After Apply: the cadence that was applied.
export const cadenceOn = (batch: ReviewBatch, edits: BatchEdits) =>
  batch.applied ? batch.applied.cadence != null : cadenceChanges(batch) && edits.cadenceIn;

export const chosenWeeks = (batch: ReviewBatch, edits: BatchEdits) =>
  cadenceOn(batch, edits) ? batch.cadence.recommended_weeks : batch.cadence.current_weeks;

export function viewRows(batch: ReviewBatch, edits: BatchEdits): ViewRow[] {
  const on = cadenceOn(batch, edits);
  const appliedBy = new Map((batch.applied?.rows ?? []).map((r) => [r.product_equivalency_id, r]));
  return batch.rows.map((row) => {
    const side = on ? row.recommended : row.current;
    const appliedRow = appliedBy.get(row.product_equivalency_id) ?? null;
    if (batch.applied) {
      const kind = appliedRow ? (appliedRow.outcome === 'applied' ? 'applied' : 'kept') : side.kind;
      return { ...row, side, kind, proposed: false, start: row.max_now, value: row.max_now, custom: false,
               included: true, appliedRow };
    }
    const proposed = PROPOSED_KINDS.includes(side.kind);
    const start = proposed ? side.engine_max : row.max_now;
    const typed = edits.values[row.product_equivalency_id];
    const value = typed ?? start;
    return {
      ...row, side, kind: side.kind, proposed, start, value,
      custom: typed != null && typed !== start,
      included: !proposed || !edits.unchecked[row.product_equivalency_id],
      appliedRow: null,
    };
  });
}

export interface Pending {
  rows: ViewRow[];
  proposed: ViewRow[];
  writes: ViewRow[]; // New max differs from the max now
  left: ViewRow[]; // a checked proposal left at the max now (closes, writes nothing)
  kept: ViewRow[]; // unchecked proposals
  custom: number;
  missingNotes: number;
  cadence: boolean;
}

export function pending(batch: ReviewBatch, edits: BatchEdits): Pending {
  const rows = viewRows(batch, edits);
  const proposed = rows.filter((r) => r.proposed);
  const writes = rows.filter((r) => r.included && r.value != null && r.value !== r.max_now && (r.proposed || r.custom));
  const left = proposed.filter((r) => r.included && r.value === r.max_now);
  const kept = proposed.filter((r) => !r.included);
  return {
    rows, proposed, writes, left, kept,
    custom: writes.filter((r) => r.custom).length,
    missingNotes: kept.filter((r) => !(edits.notes[r.product_equivalency_id] ?? '').trim()).length,
    cadence: !batch.applied && cadenceChanges(batch) && edits.cadenceIn,
  };
}

export const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;
export const every = (weeks: number) => (weeks === 1 ? 'every week' : `every ${weeks} weeks`);

// "14 of 15 proposed changes included · 1 kept with a note · 1 left at the max now · 2 from the customer"
export function countsLine(p: Pending): string {
  const parts: string[] = [];
  if (p.proposed.length) parts.push(`${p.proposed.length - p.kept.length} of ${plural(p.proposed.length, 'proposed change')} included`);
  if (p.kept.length) parts.push(`${p.kept.length} kept with a note`);
  if (p.left.length) parts.push(`${p.left.length} left at the max now`);
  if (p.custom) parts.push(`${p.custom} from the customer`);
  return parts.join(' · ');
}

// "Apply 15 max changes and the 3-week restock…"
export function applyLabel(p: Pending, batch: ReviewBatch): string {
  const what = [
    p.writes.length ? plural(p.writes.length, 'max change') : null,
    p.cadence ? `the ${batch.cadence.recommended_weeks}-week restock` : null,
  ].filter(Boolean);
  if (what.length) return `Apply ${what.join(' and ')}…`;
  return p.left.length || p.kept.length ? 'Apply…' : 'Nothing to apply';
}

// Keeps and left-at-the-max-now rows write no max but still close their
// recommendations (declined with the note / completed), so they can be applied alone.
export const canApply = (p: Pending) =>
  p.missingNotes === 0 && (p.writes.length > 0 || p.cadence || p.left.length > 0 || p.kept.length > 0);

export function applyRequest(batch: ReviewBatch, edits: BatchEdits): BatchApplyRequest {
  const cadenceChange = cadenceChanges(batch) && edits.cadenceIn;
  return {
    review_id: batch.review_id,
    cadence_change: cadenceChange,
    rows: batch.rows.map((r) => ({
      product_equivalency_id: r.product_equivalency_id,
      max_now: r.max_now,
      // The kind the user saw: Apply refuses (409) if the row changed since.
      kind: (cadenceChange ? r.recommended : r.current).kind,
      new_max: edits.values[r.product_equivalency_id] ?? null,
      included: !edits.unchecked[r.product_equivalency_id],
      note: edits.notes[r.product_equivalency_id]?.trim() || null,
    })),
  };
}

// --- dates -----------------------------------------------------------------

const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const parseDay = (iso: string) => {
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  return new Date(y, m - 1, d);
};
export const addDays = (iso: string, n: number) => {
  const d = parseDay(iso);
  d.setDate(d.getDate() + n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
// "Oct 7", with the year when it differs from the reference day's.
export const shortDay = (iso: string, refIso: string) => {
  const d = parseDay(iso);
  const sameYear = d.getFullYear() === parseDay(refIso).getFullYear();
  return `${MON[d.getMonth()]} ${d.getDate()}${sameYear ? '' : `, ${d.getFullYear()}`}`;
};
export const longDay = (iso: string) => {
  const d = parseDay(iso);
  return `${MON[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
};
// "Oct 7 at 12:20 PM" in the viewer's zone.
export const dayAndTime = (isoTime: string) => {
  const d = new Date(isoTime);
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  return `${MON[d.getMonth()]} ${d.getDate()} at ${time}`;
};
// Today in the viewer's zone, as YYYY-MM-DD.
export const todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
export const moneyCents = (cents: number) =>
  `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// --- the Why sentence -------------------------------------------------------

const usageText = (r: BatchRow) =>
  r.units ? `Used ${plural(r.units, 'unit')} in ${r.demand_weeks ?? 0} of ${r.observed_weeks} weeks.`
    : `Not used in ${r.observed_weeks} weeks.`;

const onShelf = (r: BatchRow) =>
  r.on_hand ? ` The ${r.on_hand} on hand stay${r.on_hand === 1 ? 's' : ''} on the shelf.` : '';

const tagged = (weeks: number | null) =>
  weeks == null || weeks < 1 ? 'Tagged this week.' : `Tagged ${plural(weeks, 'week')} ago.`;

// The Why for a row, from the stored numbers and the chosen cadence only.
// refDay (the review's day) decides when a date needs its year.
export function whySentence(r: ViewRow, batch: ReviewBatch, weeks: number, refDay: string): string {
  const { quiet_weeks: quietWeeks, kept_weeks: keptWeeks } = batch.windows;
  const ref = refDay;
  if (r.kind === 'applied' && batch.applied && r.appliedRow) return appliedWhy(r.appliedRow, batch.applied, batch);
  if (r.kind === 'kept' && batch.applied && r.appliedRow) return keptWhy(r.appliedRow, batch.applied, batch);

  const eng = r.side.engine_max;
  if (r.proposed) {
    return eng === 0 ? usageText(r) + onShelf(r) : `${usageText(r)} Min ${r.min}; max ${eng} when restocked ${every(weeks)}.`;
  }
  if (r.kind === 'right') {
    return usageText(r) + (r.max_now === 0 ? ' Already at 0.' : ` Min ${r.min}; the max already fits ${every(weeks)} restocks.`);
  }
  if (r.kind === 'quiet' && r.side.quiet) {
    const q = r.side.quiet;
    let would = '';
    if (eng != null && eng !== r.max_now) {
      const dir = r.max_now == null ? 'set' : eng > r.max_now ? 'raise' : 'lower';
      would = `The engine would ${dir} the max to ${eng}. `;
    }
    return q.status === 'declined'
      ? `${would}It was kept on ${shortDay(q.since, ref)}, and the engine waits ${keptWeeks} weeks before proposing that again, until ${shortDay(q.until, ref)}.`
      : `${would}The max changed ${shortDay(q.since, ref)}, and the engine waits ${quietWeeks} weeks after a change, until ${shortDay(q.until, ref)}.`;
  }
  if (r.kind === 'too_new') {
    return r.policy === 'monitoring'
      ? `${tagged(r.tenure_weeks).replace(/\.$/, '')} and not used yet. The engine waits ${batch.windows.unused_weeks} weeks before calling it occasional use.`
      : `${tagged(r.tenure_weeks)} The engine needs ${batch.windows.too_new_weeks} weeks of use first.`;
  }
  return '';
}

function appliedWhy(a: BatchAppliedRow, applied: BatchApplied, batch: ReviewBatch): string {
  const day = applied.applied_on;
  const who = applied.actor_name ?? applied.actor;
  const base = `Applied ${shortDay(day, day)} by ${who}${a.custom ? ", the customer's number" : ''}. The max was ${a.before ?? 'none'}.`;
  if (a.recommendation_id == null) return base; // no recommendation completed, so no quiet period
  const until = addDays(day, batch.windows.quiet_weeks * 7);
  return `${base} The engine waits ${batch.windows.quiet_weeks} weeks after a change, until ${shortDay(until, day)}.`;
}

function keptWhy(a: BatchAppliedRow, applied: BatchApplied, batch: ReviewBatch): string {
  const day = applied.applied_on;
  if (a.outcome === 'kept') {
    const base = `Kept on ${shortDay(day, day)}: "${a.note ?? ''}".`;
    if (a.recommendation_id == null) return base;
    const until = addDays(day, batch.windows.kept_weeks * 7);
    return `${base} The engine won't propose this again until ${longDay(until)}.`;
  }
  const base = `Left at the max now on ${shortDay(day, day)}.`;
  if (a.recommendation_id == null) return base;
  const until = addDays(day, batch.windows.quiet_weeks * 7);
  return `${base} The engine waits ${batch.windows.quiet_weeks} weeks, until ${shortDay(until, day)}.`;
}

// --- groups -----------------------------------------------------------------

export const GROUPS_BEFORE: [BatchKind, string][] = [
  ['up', 'Maxes going up'],
  ['down', 'Maxes going down'],
  ['new', 'New maxes'],
  ['occasional', 'Occasional use: max going to 0'],
  ['right', "No change: already at the engine's number"],
  ['quiet', 'No change: changed recently, quiet for now'],
  ['too_new', 'No change: too new to judge'],
];

export const groupsAfter = (appliedOn: string): [ViewRow['kind'], string][] => [
  ['applied', `Applied ${shortDay(appliedOn, appliedOn)}`],
  ['kept', 'Kept at the SYNC'],
  ...GROUPS_BEFORE.slice(4),
  // A row whose live max moved after Apply (an inventory-page edit) keeps its
  // natural group rather than vanishing.
  ...GROUPS_BEFORE.slice(0, 4),
];

// Names in natural order ("Bag 2" before "Bag 10").
export const byName = (a: ViewRow, b: ViewRow) =>
  (a.name ?? '').localeCompare(b.name ?? '', 'en', { numeric: true });

// --- the email Marah sends (UI AC 5) --------------------------------------------
// The app sends nothing (Nick 10/07): after Apply the dialog hands Marah this
// email to copy into her own mail. Fixed copy, built from what Apply wrote.

export const EMAIL_SUBJECT = 'Stocking update for your HEXA rack';

export interface EmailRow {
  name: string | null;
  outcome: 'applied' | 'kept' | 'left';
  before: number | null;
  after: number | null;
  engine_max: number | null;
}

// Before Apply: what the confirmation says Apply will write.
export const emailRowsFromPending = (p: Pending): EmailRow[] => [
  ...p.writes.map((r): EmailRow => ({ name: r.name, outcome: 'applied', before: r.max_now, after: r.value, engine_max: r.side.engine_max })),
  ...p.kept.map((r): EmailRow => ({ name: r.name, outcome: 'kept', before: r.max_now, after: r.max_now, engine_max: r.side.engine_max })),
  ...p.left.map((r): EmailRow => ({ name: r.name, outcome: 'left', before: r.max_now, after: r.max_now, engine_max: r.side.engine_max })),
];

// After Apply: exactly what the batch record says was written.
export const emailRowsFromApplied = (applied: BatchApplied): EmailRow[] => applied.rows;

// "Marah Smith" -> "Marah"; no name -> the email's first word, capitalized.
export const firstName = (name: string | null | undefined, email: string | null | undefined) => {
  if (name && name.trim()) return name.trim().split(/\s+/)[0];
  const word = (email ?? '').split('@')[0].split(/[._+-]/)[0];
  return word ? word.charAt(0).toUpperCase() + word.slice(1) : '';
};

// Occasional use = the engine's 0 applied as 0; any other write reads by
// direction against the max before.
const lineKind = (r: EmailRow) => {
  if (r.after === 0 && r.engine_max === 0) return 'occasional';
  if (r.before == null) return 'new';
  return (r.after ?? 0) > r.before ? 'up' : 'down';
};
const byRowName = (a: EmailRow, b: EmailRow) => (a.name ?? '').localeCompare(b.name ?? '', 'en', { numeric: true });

export function emailText(o: {
  locationName: string; day: string; cadence: { from: number; to: number } | null; rows: EmailRow[]; signer: string;
}): string {
  const applied = o.rows.filter((r) => r.outcome === 'applied').sort(byRowName);
  const kept = o.rows.filter((r) => r.outcome !== 'applied').sort(byRowName);
  const of = (kind: string) => applied.filter((r) => lineKind(r) === kind);
  const out = ['Hi,', '', `Following our SYNC on ${shortDay(o.day, o.day)}, we've updated the stocking levels on your HEXA rack at ${o.locationName}.`];
  if (o.cadence) {
    const to = every(o.cadence.to);
    out.push('', 'Restocking', `${to.charAt(0).toUpperCase()}${to.slice(1)} instead of ${every(o.cadence.from)}.`);
  }
  ([['up', 'Maxes going up'], ['down', 'Maxes going down'], ['new', 'New maxes']] as const).forEach(([kind, heading]) => {
    const lines = of(kind);
    if (lines.length) out.push('', heading, ...lines.map((r) => `- ${r.name}: ${r.before ?? 'none'} → ${r.after}`));
  });
  if (of('occasional').length) {
    out.push('', 'Used now and then',
      "You use these now and then, so we've stopped restocking them automatically. What's on your shelf stays. "
      + "If you'd like to keep one or two on hand for a big job, reply and we'll set that up.",
      ...of('occasional').map((r) => `- ${r.name}`));
  }
  if (kept.length) out.push('', 'Kept as they are', ...kept.map((r) => `- ${r.name}: max ${r.before ?? 'none'}`));
  out.push('', "Nothing you need to do. Reply if you'd like to change anything.", '', o.signer, 'HEXA');
  return out.join('\n');
}
